import { useEffect } from "react";

interface SeoOpts {
  title?: string;
  description?: string;
  /** Relative or absolute path; defaults to current location. */
  canonical?: string;
}

/**
 * Updates document title, meta description, canonical, and OG tags
 * for the current route. Restores nothing on unmount — the next route
 * just sets its own. For social-media unfurls (Twitterbot/Facebook),
 * the static defaults in index.html are what gets read.
 */
export function useSeo({ title, description, canonical }: SeoOpts) {
  useEffect(() => {
    if (title) document.title = title;
    if (description) setMeta("name", "description", description);
    setMetaProp("og:title", title);
    setMetaProp("og:description", description);
    setLink("canonical", canonical ?? window.location.origin + window.location.pathname);
    setMetaProp("og:url", canonical ?? window.location.origin + window.location.pathname);
  }, [title, description, canonical]);
}

function setMeta(attr: "name" | "property", key: string, value: string | undefined) {
  if (!value) return;
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", value);
}

function setMetaProp(key: string, value: string | undefined) {
  setMeta("property", key, value);
}

function setLink(rel: string, href: string) {
  let el = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.rel = rel;
    document.head.appendChild(el);
  }
  el.href = href;
}
